import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { quotationSuppliersEndpoint } from 'apis/restaurant-inquiries/use-quotation-suppliers-api';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useQueryClient } from 'react-query';

import type { QuotationSupplierProduct } from './types';

type TData = { data: QuotationSupplierProduct };
type TError = AxiosError;
type TVariables = { supplierId: number; itemId: number; masterProductId?: number };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Deferred create-and-link: POST .../quotation-suppliers/:supplierId/products/from-master.
 * Adds the inquiry item's master product to the supplier's public catalog
 * (master-owned fields copied, master_product_id set). Called by the quote
 * workbench ONLY while saving quotes — never on the "does this supplier carry
 * it?" click. Idempotent server-side; no success toast (it's a step inside the
 * quote save, which has its own summary toast).
 */
export function useCreateQuotationProductFromMasterApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ supplierId, itemId, masterProductId }: TVariables) => {
    try {
      // masterProductId = similar-variant override (a sibling variant of the
      // item's master family the supplier actually carries); omitted = exact.
      const { data } = await http.post(`${quotationSuppliersEndpoint}/${supplierId}/products/from-master`, {
        itemId,
        masterProductId,
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [`${quotationSuppliersEndpoint}/products/from-master`],
    onSuccess: () => {
      queryClient.invalidateQueries([quotationSuppliersEndpoint]);
    },
    ...options,
  });
}
